import { useCallback, useMemo, useState } from "react";

export interface PaginationState {
  currentPage: number;
  pageSize: number;
  paginationParams: {
    page: number;
    pageSize: number;
  };
}

interface UsePaginationProps {
  initialPage?: number;
  initialPageSize?: number;
}

export const usePagination = ({
  initialPage = 1,
  initialPageSize = 10,
}: UsePaginationProps = {}): PaginationState & {
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (size: number) => void;
  setCurrentPage: (page: number) => void;
} => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);

  // Memoize the pagination parameters for API calls
  const paginationParams = useMemo(
    () => ({
      page: currentPage,
      pageSize: pageSize,
    }),
    [currentPage, pageSize],
  );

  return {
    currentPage,
    pageSize,
    paginationParams,
    handlePageChange,
    handlePageSizeChange,
    setCurrentPage,
  };
};
export type Pagination = ReturnType<typeof usePagination>;
